/*
 * PERMISSION HOOKS
 * 
 * Provides reusable hooks for checking permissions on accounts and contacts.
 * Centralizes permission logic and provides consistent interfaces for components.
 * 
 * Features:
 * - useEntityPermissions: General hook for any entity with permission data
 * - useAccountPermissions: Specific hook for account entities
 * - useContactPermissions: Specific hook for contact entities
 * - Consistent return structure with edit/delete permissions and descriptions
 * 
 * Dependencies:
 * - src/utils/permissions.ts for core permission logic
 * - src/context/TeamContext for current user and team data
 */

import { useMemo } from 'react';
import { useTeams } from '../context/TeamContext';
import { canEdit, canDelete, getPermissionDescription, type PermissionCheckData } from '../utils/permissions';

/**
 * Permission state returned by permission hooks
 */
export interface PermissionState {
  canEdit: boolean;
  canDelete: boolean;
  description: string;
  isLoading: boolean;
}

/**
 * General permission hook for any entity that implements PermissionCheckData
 */
export function useEntityPermissions<T extends PermissionCheckData>(entity: T | null): PermissionState {
  const { teams, currentUserId, isLoading } = useTeams();

  return useMemo(() => {
    if (isLoading || !entity || !currentUserId) {
      return {
        canEdit: false,
        canDelete: false,
        description: 'Loading...',
        isLoading: true
      };
    }

    const hasEditPermission = canEdit(entity, teams, currentUserId);
    const hasDeletePermission = canDelete(entity, currentUserId);
    const permissionDescription = getPermissionDescription(entity, teams, currentUserId);

    return {
      canEdit: hasEditPermission,
      canDelete: hasDeletePermission,
      description: permissionDescription,
      isLoading: false
    };
  }, [entity, teams, currentUserId, isLoading]);
}

/**
 * Specific permission hook for account entities
 * Provides type-safe interface for Account objects
 */
export function useAccountPermissions(account: PermissionCheckData | null): PermissionState {
  return useEntityPermissions(account);
}

/**
 * Specific permission hook for contact entities  
 * Provides type-safe interface for Contact objects
 */
export function useContactPermissions(contact: PermissionCheckData | null): PermissionState {
  return useEntityPermissions(contact);
}

/**
 * Hook for checking permissions on multiple entities
 * Useful for bulk operations or table selection
 */
export function useMultiEntityPermissions<T extends PermissionCheckData>(entities: T[]): {
  canEditAny: boolean;
  canDeleteAny: boolean;
  canEditAll: boolean;
  canDeleteAll: boolean;
  editableCount: number;
  deletableCount: number;
  isLoading: boolean;
} {
  const { teams, currentUserId, isLoading } = useTeams();

  return useMemo(() => {
    if (isLoading || !currentUserId || entities.length === 0) {
      return {
        canEditAny: false,
        canDeleteAny: false,
        canEditAll: false,
        canDeleteAll: false,
        editableCount: 0,
        deletableCount: 0,
        isLoading: true
      };
    }

    const permissions = entities.map(entity => ({
      canEdit: canEdit(entity, teams, currentUserId),
      canDelete: canDelete(entity, currentUserId)
    }));

    const editableCount = permissions.filter(p => p.canEdit).length;
    const deletableCount = permissions.filter(p => p.canDelete).length;

    return {
      canEditAny: editableCount > 0,
      canDeleteAny: deletableCount > 0,
      canEditAll: editableCount === entities.length,
      canDeleteAll: deletableCount === entities.length,
      editableCount,
      deletableCount,
      isLoading: false
    };
  }, [entities, teams, currentUserId, isLoading]);
}